// src/context/AppContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import ApiService from '../services/api';
import TransactionApiService from '../services/transactionApi';
import AuthService from '../services/authService';

// Create the context
const AppContext = createContext();

// Custom hook to use the context
export const useAppContext = () => useContext(AppContext);

// Provider component
export const AppProvider = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Financial data
    const [runwayData, setRunwayData] = useState(null);
    const [projectionData, setProjectionData] = useState(null);
    const [funMoneyData, setFunMoneyData] = useState(null);
    const [funMoneySettings, setFunMoneySettings] = useState(null);
    const [discretionarySpending, setDiscretionarySpending] = useState(null);
    const [pendingTransactions, setPendingTransactions] = useState([]);
    const [hasPendingTransactions, setHasPendingTransactions] = useState(false);
    const [budgetCategories, setBudgetCategories] = useState([]);
    const [isCheckingTransactions, setIsCheckingTransactions] = useState(false);
    const [transactionBlockingEnabled, setTransactionBlockingEnabled] = useState(true);

    // Check authentication status on mount
    useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                setIsCheckingAuth(true);
                const response = await AuthService.checkStatus();
                setIsAuthenticated(response.data && response.data.authenticated);
            } catch (error) {
                console.error('Error checking auth status:', error);
                setIsAuthenticated(false);
            } finally {
                setIsCheckingAuth(false);
            }
        };

        checkAuthStatus();
    }, []);

    // Handle login - wrapped in useCallback
    const login = useCallback(async (email, password) => {
        try {
            const response = await AuthService.login(email, password);
            setIsAuthenticated(response.data && response.data.authenticated);
            return response.data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }, []);

    // Handle MFA verification - wrapped in useCallback
    const verifyMfa = useCallback(async (mfaCode) => {
        try {
            const response = await AuthService.verifyMfa(mfaCode);
            setIsAuthenticated(response.data && response.data.authenticated);
            return response.data;
        } catch (error) {
            console.error('MFA verification error:', error);
            throw error;
        }
    }, []);

    // Handle logout - wrapped in useCallback
    const logout = useCallback(async () => {
        try {
            await AuthService.logout();
            setIsAuthenticated(false);
            // Clear all data
            setRunwayData(null);
            setProjectionData(null);
            setFunMoneyData(null);
            setFunMoneySettings(null);
            setDiscretionarySpending(null);
            setPendingTransactions([]);
            setHasPendingTransactions(false);
            setBudgetCategories([]);
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    }, []);

    // Load all initial data - wrapped in useCallback
    const loadData = useCallback(async () => {
        if (!isAuthenticated) return;

        setLoading(true);
        setError(null);

        try {
            // Fetch all data in parallel
            const [
                runwayResponse,
                projectionResponse,
                funMoneyResponse,
                funMoneySettingsResponse,
                discretionaryResponse
            ] = await Promise.all([
                ApiService.getRunway(),
                ApiService.getProjection(),
                ApiService.getFunMoneyBalances(),
                ApiService.getFunMoneySettings(),
                ApiService.getDiscretionarySpending()
            ]);

            // Update state with fetched data
            setRunwayData(runwayResponse.data);
            setProjectionData(projectionResponse.data);
            setFunMoneyData(funMoneyResponse.data);
            setFunMoneySettings(funMoneySettingsResponse.data);
            setDiscretionarySpending(discretionaryResponse.data);

            // Set last updated timestamp
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error loading application data:', err);
            setError('Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    // Function to check for pending transactions - wrapped in useCallback
    const checkPendingTransactions = useCallback(async () => {
        if (!isAuthenticated) return [];

        try {
            setIsCheckingTransactions(true);
            const response = await TransactionApiService.getPendingTransactions();

            // Handle response safely
            const transactions = response?.data?.transactions || [];
            setPendingTransactions(transactions);
            setHasPendingTransactions(transactions.length > 0);

            return transactions;
        } catch (err) {
            console.error('Error checking pending transactions:', err);
            return [];
        } finally {
            setIsCheckingTransactions(false);
        }
    }, [isAuthenticated]);

    // Function to acknowledge a transaction - wrapped in useCallback
    const acknowledgeTransaction = useCallback(async (transactionId, painLevel, budgetCategory) => {
        if (!isAuthenticated) throw new Error('Not authenticated');

        try {
            const response = await TransactionApiService.acknowledgeTransaction(
                transactionId,
                painLevel,
                budgetCategory
            );

            // Update pending transactions list
            setPendingTransactions(prevTransactions =>
                prevTransactions.filter(tx => tx.id !== transactionId)
            );

            // Update hasPendingTransactions based on the new state
            setPendingTransactions(prevTransactions => {
                const newLength = prevTransactions.length;
                setHasPendingTransactions(newLength > 0);
                return prevTransactions;
            });

            return response.data;
        } catch (err) {
            console.error('Error acknowledging transaction:', err);
            throw err;
        }
    }, [isAuthenticated]);

    // Function to load budget categories - wrapped in useCallback
    const loadBudgetCategories = useCallback(async () => {
        if (!isAuthenticated) return [];

        try {
            const response = await TransactionApiService.getBudgets();

            // Handle response safely
            const budgets = response?.data?.budgets || [];
            setBudgetCategories(budgets);

            return budgets;
        } catch (err) {
            console.error('Error loading budget categories:', err);
            return [];
        }
    }, [isAuthenticated]);

    // Toggle transaction blocking feature - wrapped in useCallback
    const toggleTransactionBlocking = useCallback((isEnabled) => {
        setTransactionBlockingEnabled(isEnabled);
        // Save preference to localStorage
        localStorage.setItem('transactionBlockingEnabled', isEnabled.toString());
    }, []);

    // Load user preference for transaction blocking on initial mount
    useEffect(() => {
        const savedPreference = localStorage.getItem('transactionBlockingEnabled');
        if (savedPreference !== null) {
            setTransactionBlockingEnabled(savedPreference === 'true');
        }
    }, []);

    // Load data when authentication status changes
    useEffect(() => {
        if (isAuthenticated && !isCheckingAuth) {
            loadData();
        }
    }, [isAuthenticated, isCheckingAuth, loadData]);

    // Check for pending transactions periodically - only when authenticated
    useEffect(() => {
        let interval;

        const checkTransactionsWrapper = async () => {
            if (isAuthenticated) {
                try {
                    const transactions = await checkPendingTransactions();
                    // Also load budget categories if we have transactions
                    if (transactions && transactions.length > 0) {
                        await loadBudgetCategories();
                    }
                } catch (error) {
                    // Silently fail but log the error
                    console.error('Failed to check transactions, will try again later:', error);
                }
            }
        };

        // Only start checking when authenticated and not in the authentication check process
        if (isAuthenticated && !isCheckingAuth) {
            // Initial check - with delay to avoid race conditions
            const initialCheck = setTimeout(checkTransactionsWrapper, 2000);

            // Set up interval for checking (every 2 minutes)
            interval = setInterval(checkTransactionsWrapper, 2 * 60 * 1000);

            return () => {
                clearTimeout(initialCheck);
                clearInterval(interval);
            };
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isAuthenticated, isCheckingAuth, checkPendingTransactions, loadBudgetCategories]);

    // Refresh data from backend - wrapped in useCallback
    const refreshData = useCallback(async () => {
        if (!isAuthenticated) return false;

        try {
            await ApiService.refreshData();
            await loadData();
            return true;
        } catch (err) {
            console.error('Error refreshing data:', err);
            setError('Failed to refresh data from Monarch Money.');
            return false;
        }
    }, [isAuthenticated, loadData]);

    // Add a new fun money transaction - wrapped in useCallback
    const addFunMoneyTransaction = useCallback(async (transaction) => {
        if (!isAuthenticated) throw new Error('Not authenticated');

        try {
            const response = await ApiService.addFunMoneyTransaction(transaction);

            // Refresh fun money data
            const funMoneyResponse = await ApiService.getFunMoneyBalances();
            setFunMoneyData(funMoneyResponse.data);

            return response.data;
        } catch (err) {
            console.error('Error adding fun money transaction:', err);
            throw err;
        }
    }, [isAuthenticated]);

    // Update fun money settings - wrapped in useCallback
    const updateFunMoneySettings = useCallback(async (settings) => {
        if (!isAuthenticated) throw new Error('Not authenticated');

        try {
            const response = await ApiService.updateFunMoneySettings(settings);
            setFunMoneySettings(response.data?.settings || null);
            return response.data;
        } catch (err) {
            console.error('Error updating fun money settings:', err);
            throw err;
        }
    }, [isAuthenticated]);

    // Value object to be provided to consumers
    const value = {
        // Authentication state
        isAuthenticated,
        loading: loading || isCheckingAuth,
        login,
        verifyMfa,
        logout,

        // Existing state and functions
        pendingTransactions,
        hasPendingTransactions,
        budgetCategories,
        isCheckingTransactions,
        transactionBlockingEnabled,
        error,
        lastUpdated,
        runwayData,
        projectionData,
        funMoneyData,
        funMoneySettings,
        discretionarySpending,
        loadData,
        refreshData,
        addFunMoneyTransaction,
        updateFunMoneySettings,
        checkPendingTransactions,
        acknowledgeTransaction,
        loadBudgetCategories,
        toggleTransactionBlocking,
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};